




















































































































































































import { Vue, Component, Prop } from "@/shared/vuepack";
import { useContactInfoStore } from "@/shared/store/contactinfo";
import { useThemeStore } from "@/shared/store/theme";
import {
  ContactInformation,
  EmployerEmployeeProfileSettingValue,
  MaritalStatus
} from "@/shared/types";
import UsStateSelector from "@/shared/components/UsStateSelector.vue";
import {
  verifyHsabEmailChange,
  sendHsabEmailChangeVerificationCode
} from "@/shared/api/userService";
import ResendCodeCard from "@/shared/components/ResendCodeCard.vue";
import VerificationCode from "@/employee/components/shared/VerificationCode.vue";
import { useNotifierStore } from "@/shared/store/notifier";
import GenericLoadingSpinner from "@/shared/components/ui/GenericLoadingSpinner.vue";
import AddressForm from "@/employee/components/settings/profile/AddressForm.vue";
import { useAddressFormStore } from "@/employee/store/addressForm";

const fieldDisabledMessage = "This field has been locked by your employer. Please contact your employer directly to make changes.";

@Component({
  components: {
    UsStateSelector,
    ResendCodeCard,
    VerificationCode,
    GenericLoadingSpinner,
    AddressForm
  }
})
export default class EditProfile extends Vue {
  contactInfoStore = useContactInfoStore();
  themeStore = useThemeStore();
  notifierStore = useNotifierStore();
  addressFormStore = useAddressFormStore();
  MaritalStatus = MaritalStatus;
  isSubmitting = false;
  isSendingVerificationCode = false;
  isVerifyingEmailChange = false;
  fieldDisabledMessage = fieldDisabledMessage;
  showProfileEditStep = true;
  showVerificationStep = false;
  verificationCode = "";
  isModalScrollable = false;

  @Prop({ required: true })
  contactInfo: ContactInformation;

  @Prop({ required: true })
  onSave: () => Promise<void>;

  @Prop({ required: true })
  toggleClose: () => void;

  @Prop({ required: true })
  show: { value: boolean };

  get getModalSize () {
    if (this.showVerificationStep) {
      return "";
    }
    return "xl";
  }

  get isProcessing () {
    return this.isSendingVerificationCode || this.isVerifyingEmailChange || this.isSubmitting;
  }

  get submitDisabled () {
    return !this.isValid || this.isProcessing || this.addressFormStore.saveDisabled;
  }

  get isValid () {
    return !this.$validator.errors.any() && this.addressFormStore.isValid;
  }

  get fieldEditSettings () {
    return this.contactInfoStore.contactInformation.fieldEditSettings;
  }

  get initialEmailAddress () {
    if (!this.contactInfoStore.contactInformation || !this.contactInfoStore.contactInformation.email) {
      return null;
    }

    return this.contactInfoStore.contactInformation.email.toLowerCase();
  }

  emailAddressChanged () {
    if (this.contactInfo.email) {
      return this.contactInfo.email.toLowerCase() !== this.initialEmailAddress;
    }

    return false;
  }

  sendVerificationCode (args: { resend: boolean}): Promise<void> {
    const unableToSendCodeMessage = "Unable to send email verification code. Please try again.";
    this.verificationCode = "";
    this.isSendingVerificationCode = true;
    return sendHsabEmailChangeVerificationCode({ email: this.contactInfo.email })
      .then(resp => {
        if (resp && resp.data && resp.data.success) {
          if (args.resend) {
            this.notifierStore.setSuccess("Verification code has been resent.");
          }
        } else {
          this.notifierStore.setDanger(unableToSendCodeMessage);
        }
      })
      .catch(_ => {
        this.notifierStore.setDanger(unableToSendCodeMessage);
      }).finally(() => {
        this.isSendingVerificationCode = false;
    });
  }

  verifyEmailChange () {
    this.validateAll().then(result => {
      if (!result) {
        return;
      }
      this.isVerifyingEmailChange = true;

      verifyHsabEmailChange({
        verificationCode: this.verificationCode
      })
      .then(resp => {
        if (resp && resp.data && resp.data.success) {
          return this.save();
        } else {
          this.notifierStore.setDanger("Unable to verify your email. Please try again.");
        }
      })
      .catch(err => {
        const errMsg = this.getErrorMessage(err);
        this.notifierStore.setDanger(errMsg);
      }).finally(() => {
        this.isVerifyingEmailChange = false;
      });
    });
  }

  validateAll () {
    const validations = [
      this.$validator.validateAll(),
      this.validateAddress()
    ];

    return Promise.all(validations).then(results => {
      return results.every(result => result === true);
    });
  }

  validateAddress (): Promise<boolean> {
    const addressForm = this.getAddressComponent();

    if (addressForm) {
      return addressForm.validate();
    }

    return Promise.resolve(true);
  }

  getErrorMessage (err: any) {
    return err && err.response && err.response.data && err.response.data.errorMessage
        ? err.response.data.errorMessage
        : "Something went wrong.";
  }

  next () {
    this.validateAll().then(result => {
      if (!result) {
        return;
      }

      this.verifyAddress()
        .then(() => {
          const emailAddressChanged = this.emailAddressChanged();

          if (emailAddressChanged) {
            this.sendVerificationCode({ resend: false })
              .then(() => {
                this.showProfileEditStep = false;
                this.showVerificationStep = true;
              });

            return;
          }

          this.save();
        })
        .catch(() => {/*noop*/});
    });
  }

  verifyAddress (): Promise<void> {
    const addressForm = this.getAddressComponent();

    if (addressForm) {
      return addressForm.verifyAddress();
    }

    return Promise.resolve();
  }

  save (): Promise<void> {
   return this.validateAll().then(result => {
      if (!result) {
        return;
      }

      this.isSubmitting = true;

      return this.onSave().finally(() => {
        this.isSubmitting = false;
      });
    });
  }

  isFieldDisabled (value: EmployerEmployeeProfileSettingValue) {
    return value !== EmployerEmployeeProfileSettingValue.Edit;
  }

  goBackToEditProfile () {
    this.resetEditProfileVariables();
  }

  onClose () {
    this.resetEditProfileVariables();
  }

  resetEditProfileVariables () {
    this.showVerificationStep = false;
    this.showProfileEditStep = true;
    this.verificationCode = "";
    this.addressFormStore.resetState();
  }

  getAddressComponent () {
    return this.$refs.addressForm as any;
  }

  mounted () {
    this.setModalScrollable();
    window.addEventListener("resize", this.setModalScrollable);
  }

  setModalScrollable () {
    const smallScreenMaxBreakpoint = 768;
    this.isModalScrollable = window.innerWidth < smallScreenMaxBreakpoint;
  }

  beforeDestroy () {
    window.removeEventListener("resize", this.setModalScrollable);
  }
}
